import { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import * as faceapi from 'face-api.js';
import { LOGOSVG } from '../../assets';
import {
  estimateHeadPose,
  poseMatchesTarget,
  type HeadPoseDir,
} from '../../utils/faceWellness/headPose';
import {
  estimateFromRgbSeries,
  sampleForeheadRgb,
} from '../../utils/faceWellness/rppgPos';
import {
  computeStressScore,
  topEmotionKey,
  type FaceExpressions,
  type StressLevelKey,
} from '../../utils/faceWellness/stressScore';
import './face-wellness-page.css';

const SCAN_SEC = 30;
const MODEL_URL =
  'https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js@0.22.2/weights';
const RING_SIZE = 88;
const RING_STROKE = 5;
const RING_R = (RING_SIZE - RING_STROKE) / 2;
const RING_C = 2 * Math.PI * RING_R;
const UI_TICK_MS = 100;
const POSE_SEQUENCE: Array<Exclude<HeadPoseDir, 'unknown'>> = [
  'left',
  'right',
  'up',
  'down',
  'front',
];
const POSE_HOLD_MS = 900;
const POSE_STEP_MAX_MS = 5500;
const POSE_DETECT_MS = 280;

type Phase = 'idle' | 'preparing' | 'scanning' | 'analyzing' | 'done' | 'error';
type PoseGuideStep = Exclude<HeadPoseDir, 'unknown'>;

type Result = {
  age: number | null;
  gender: 'male' | 'female' | null;
  heartRateBpm: number | null;
  rmssdMs: number | null;
  stressScore: number;
  stressLevel: StressLevelKey;
  topEmotion: keyof FaceExpressions | null;
};

function CameraIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="56"
      height="56"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M4 8.5A2.5 2.5 0 0 1 6.5 6h2.1l1.2-1.8A1.5 1.5 0 0 1 11 3.5h2a1.5 1.5 0 0 1 1.2.7L15.4 6h2.1A2.5 2.5 0 0 1 20 8.5v9A2.5 2.5 0 0 1 17.5 20h-11A2.5 2.5 0 0 1 4 17.5v-9Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="13" r="3.25" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function PoseArrow({ dir }: { dir: PoseGuideStep }) {
  if (dir === 'front') {
    return (
      <svg className="face-wellness__pose-arrow-svg" viewBox="0 0 64 64" aria-hidden>
        <circle cx="32" cy="32" r="10" fill="currentColor" opacity="0.9" />
        <circle
          cx="32"
          cy="32"
          r="18"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeDasharray="4 5"
        />
      </svg>
    );
  }
  const rot =
    dir === 'left' ? 180 : dir === 'right' ? 0 : dir === 'up' ? -90 : 90;
  return (
    <svg
      className="face-wellness__pose-arrow-svg"
      viewBox="0 0 64 64"
      style={{ transform: `rotate(${rot}deg)` }}
      aria-hidden
    >
      <path
        d="M12 32h28M30 18l16 14-16 14"
        fill="none"
        stroke="currentColor"
        strokeWidth="5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function FaceWellnessPage() {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sampleCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rgbSeriesRef = useRef<Array<{ r: number; g: number; b: number }>>([]);
  const scanStartedAt = useRef(0);
  const uiTickRef = useRef(0);
  const sampleRafRef = useRef(0);
  const ringProgressRef = useRef<SVGCircleElement>(null);
  const poseTimerRef = useRef(0);
  const modelsReadyRef = useRef(false);
  const finishRef = useRef<() => void>(() => undefined);
  const finishingRef = useRef(false);
  const poseStepRef = useRef(0);
  const poseHoldStartRef = useRef<number | null>(null);
  const poseStepStartedAtRef = useRef(0);
  const poseFlashUntilRef = useRef(0);
  const tRef = useRef(t);
  tRef.current = t;

  const [phase, setPhase] = useState<Phase>('idle');
  const [remainSec, setRemainSec] = useState(SCAN_SEC);
  const [scanProgress, setScanProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const [hasStream, setHasStream] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const [poseStep, setPoseStep] = useState<PoseGuideStep>('left');
  const [poseMatched, setPoseMatched] = useState(false);
  const [poseFlash, setPoseFlash] = useState(false);
  const [poseHoldPct, setPoseHoldPct] = useState(0);

  const stopPoseLoop = useCallback(() => {
    if (poseTimerRef.current) {
      window.clearInterval(poseTimerRef.current);
      poseTimerRef.current = 0;
    }
  }, []);

  const stopUiTick = useCallback(() => {
    if (uiTickRef.current) {
      window.clearInterval(uiTickRef.current);
      uiTickRef.current = 0;
    }
  }, []);

  const stopSampleLoop = useCallback(() => {
    if (sampleRafRef.current) {
      cancelAnimationFrame(sampleRafRef.current);
      sampleRafRef.current = 0;
    }
  }, []);

  const applyRingProgress = useCallback((pct: number) => {
    const el = ringProgressRef.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(100, pct));
    el.style.strokeDashoffset = String(RING_C * (1 - clamped / 100));
  }, []);

  const stopCamera = useCallback(() => {
    stopUiTick();
    stopPoseLoop();
    stopSampleLoop();
    streamRef.current?.getTracks().forEach((tr) => tr.stop());
    streamRef.current = null;
    setHasStream(false);
    if (videoRef.current) videoRef.current.srcObject = null;
  }, [stopPoseLoop, stopSampleLoop, stopUiTick]);

  useEffect(() => () => stopCamera(), [stopCamera]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
          faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
          faceapi.nets.ageGenderNet.loadFromUri(MODEL_URL),
          faceapi.nets.faceExpressionNet.loadFromUri(MODEL_URL),
        ]);
        if (!cancelled) modelsReadyRef.current = true;
      } catch {
        if (!cancelled) {
          setErrorMsg(tRef.current('faceWellness.errorModelLoad'));
          setPhase('error');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const advancePoseStep = useCallback(() => {
    const next = poseStepRef.current + 1;
    poseFlashUntilRef.current = performance.now() + 700;
    setPoseFlash(true);
    poseHoldStartRef.current = null;
    setPoseHoldPct(0);
    setPoseMatched(false);
    if (next >= POSE_SEQUENCE.length) {
      poseStepRef.current = POSE_SEQUENCE.length - 1;
      setPoseStep('front');
      return;
    }
    poseStepRef.current = next;
    poseStepStartedAtRef.current = performance.now();
    setPoseStep(POSE_SEQUENCE[next]);
  }, []);

  const finishScan = useCallback(async () => {
    if (finishingRef.current) return;
    finishingRef.current = true;
    stopUiTick();
    stopPoseLoop();
    stopSampleLoop();

    setRemainSec(0);
    setScanProgress(100);
    applyRingProgress(100);
    setPhase('analyzing');

    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });

    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) {
      setPhase('error');
      setErrorMsg(tRef.current('faceWellness.errorNoCamera'));
      stopCamera();
      finishingRef.current = false;
      return;
    }

    const durationSec = (performance.now() - scanStartedAt.current) / 1000;
    const rppg = estimateFromRgbSeries(rgbSeriesRef.current, durationSec);

    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setPhase('error');
      setErrorMsg(tRef.current('faceWellness.errorCanvas'));
      stopCamera();
      finishingRef.current = false;
      return;
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    let age: number | null = null;
    let gender: 'male' | 'female' | null = null;
    let expressions: FaceExpressions | null = null;
    let topEmotion: keyof FaceExpressions | null = null;

    try {
      const det = await faceapi
        .detectSingleFace(
          canvas,
          new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.4 }),
        )
        .withAgeAndGender()
        .withFaceExpressions();
      if (det) {
        age = Math.round(det.age);
        gender = det.gender === 'male' ? 'male' : 'female';
        expressions = det.expressions as unknown as FaceExpressions;
        topEmotion = topEmotionKey(expressions);
        // 표시용: 여성은 연령대별 가중치로 나이 낮춤
        if (gender === 'female' && age != null) {
          const roll = (lo: number, hi: number) =>
            lo + Math.floor(Math.random() * (hi - lo + 1));
          let shave = 0;
          if (age < 20) shave = roll(2, 3);
          else if (age < 30) shave = roll(3, 4);
          else if (age < 40) shave = roll(4, 5);
          else shave = roll(5, 6);
          age = Math.max(1, age - shave);
        }
      }
    } catch {
      // age/emotion optional
    }

    const stress = computeStressScore(rppg.rmssdMs, expressions);
    setResult({
      age,
      gender,
      heartRateBpm: rppg.heartRateBpm,
      rmssdMs: rppg.rmssdMs,
      stressScore: stress.score,
      stressLevel: stress.level,
      topEmotion,
    });

    setPhase('done');
    finishingRef.current = false;
  }, [applyRingProgress, stopCamera, stopPoseLoop, stopSampleLoop, stopUiTick]);

  finishRef.current = () => {
    void finishScan();
  };

  const startUiTick = useCallback(() => {
    stopUiTick();
    const tick = () => {
      if (finishingRef.current) return;
      const elapsed = (performance.now() - scanStartedAt.current) / 1000;
      const pct = Math.min(100, (elapsed / SCAN_SEC) * 100);
      applyRingProgress(pct);
      setScanProgress(pct);
      setRemainSec(Math.max(0, Math.ceil(SCAN_SEC - elapsed)));
      if (performance.now() > poseFlashUntilRef.current) {
        setPoseFlash(false);
      }
      if (elapsed >= SCAN_SEC) {
        finishRef.current();
      }
    };
    tick();
    uiTickRef.current = window.setInterval(tick, UI_TICK_MS);
  }, [applyRingProgress, stopUiTick]);

  const startSampleLoop = useCallback(() => {
    stopSampleLoop();
    if (!sampleCanvasRef.current) {
      sampleCanvasRef.current = document.createElement('canvas');
    }
    const sample = () => {
      if (finishingRef.current) return;
      const video = videoRef.current;
      const canvas = sampleCanvasRef.current;
      if (video && canvas && video.readyState >= 2) {
        const w = video.videoWidth;
        const h = video.videoHeight;
        if (w && h) {
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          if (ctx) {
            ctx.drawImage(video, 0, 0, w, h);
            rgbSeriesRef.current.push(sampleForeheadRgb(ctx, w, h));
          }
        }
      }
      sampleRafRef.current = requestAnimationFrame(sample);
    };
    sampleRafRef.current = requestAnimationFrame(sample);
  }, [stopSampleLoop]);

  const runPoseDetect = useCallback(async () => {
    if (finishingRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState < 2) return;

    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return;

    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, w, h);

    try {
      const det = await faceapi
        .detectSingleFace(
          canvas,
          new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.4 }),
        )
        .withFaceLandmarks();

      const target = POSE_SEQUENCE[poseStepRef.current] ?? 'front';
      const now = performance.now();

      // Soft timeout: skip stuck steps so 30s HR scan still finishes
      if (
        target !== 'front' &&
        now - poseStepStartedAtRef.current > POSE_STEP_MAX_MS
      ) {
        advancePoseStep();
        return;
      }

      if (!det) {
        poseHoldStartRef.current = null;
        setPoseMatched(false);
        setPoseHoldPct(0);
        return;
      }

      // Landmarks are from the unmirrored camera buffer. estimateHeadPose
      // already maps subject-left/right to 'left'/'right' for mirrored selfie UX.
      // Do NOT flip again — that made left/right feel identical / swapped.
      const pose = estimateHeadPose(det.landmarks);
      const ok = poseMatchesTarget(pose.direction, target);
      setPoseMatched(ok);

      if (ok) {
        if (poseHoldStartRef.current == null) poseHoldStartRef.current = now;
        const held = now - poseHoldStartRef.current;
        setPoseHoldPct(Math.min(100, (held / POSE_HOLD_MS) * 100));
        if (target !== 'front' && held >= POSE_HOLD_MS) {
          advancePoseStep();
        }
      } else {
        poseHoldStartRef.current = null;
        setPoseHoldPct(0);
      }
    } catch {
      // ignore intermittent detect failures
    }
  }, [advancePoseStep]);

  const startScan = async () => {
    setResult(null);
    setErrorMsg('');
    setRemainSec(SCAN_SEC);
    setScanProgress(0);
    finishingRef.current = false;
    rgbSeriesRef.current = [];
    poseStepRef.current = 0;
    poseHoldStartRef.current = null;
    poseStepStartedAtRef.current = 0;
    poseFlashUntilRef.current = 0;
    setPoseStep('left');
    setPoseMatched(false);
    setPoseFlash(false);
    setPoseHoldPct(0);
    applyRingProgress(0);
    setPhase('preparing');

    try {
      stopPoseLoop();
      stopUiTick();
      stopSampleLoop();

      if (!streamRef.current) {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        streamRef.current = stream;
        setHasStream(true);
        const video = videoRef.current;
        if (!video) throw new Error('no video');
        video.srcObject = stream;
        await video.play();
      }

      const deadline = Date.now() + 25000;
      while (!modelsReadyRef.current && Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 100));
      }
      if (!modelsReadyRef.current) {
        throw new Error('models timeout');
      }

      if (!videoRef.current || !streamRef.current) throw new Error('no video');

      scanStartedAt.current = performance.now();
      poseStepStartedAtRef.current = performance.now();
      setPhase('scanning');
      startUiTick();
      startSampleLoop();
      poseTimerRef.current = window.setInterval(() => {
        void runPoseDetect();
      }, POSE_DETECT_MS);
    } catch (e) {
      stopCamera();
      setPhase('error');
      const msg =
        e instanceof Error && e.message === 'models timeout'
          ? t('faceWellness.errorModelTimeout')
          : t('faceWellness.errorCameraPermission');
      setErrorMsg(msg);
    }
  };

  const handleShutter = () => {
    if (phase === 'idle' || phase === 'done' || phase === 'error') {
      void startScan();
      return;
    }
    if (phase === 'scanning') {
      stopCamera();
      setPhase('idle');
      setScanProgress(0);
      applyRingProgress(0);
      setRemainSec(SCAN_SEC);
      setPoseMatched(false);
      setPoseFlash(false);
      setPoseHoldPct(0);
    }
  };

  const showPlaceholder = !hasStream || phase === 'idle' || phase === 'error';
  const showAnalyzing = phase === 'analyzing' || phase === 'preparing';
  const showFaceGuide =
    hasStream && (phase === 'preparing' || phase === 'scanning' || phase === 'analyzing');

  const genderText =
    result?.gender === 'male'
      ? t('faceWellness.genderMale')
      : result?.gender === 'female'
        ? t('faceWellness.genderFemale')
        : null;

  const emotionText = result?.topEmotion
    ? t(`faceWellness.emotion.${result.topEmotion}`)
    : null;

  return (
    <div className="face-wellness">
      <Helmet>
        <title>{t('faceWellness.metaTitle')}</title>
        <meta name="robots" content="noindex" />
        <meta name="description" content={t('faceWellness.metaDescription')} />
      </Helmet>

      <div className="face-wellness__viewport">
        <video ref={videoRef} className="face-wellness__video" playsInline muted autoPlay />
        <canvas ref={canvasRef} className="face-wellness__canvas" aria-hidden />

        {showPlaceholder ? (
          <div className="face-wellness__placeholder" aria-hidden>
            <CameraIcon className="face-wellness__cam-icon" />
          </div>
        ) : null}

        {showFaceGuide ? (
          <div
            className={`face-wellness__face-guide${
              poseMatched ? ' face-wellness__face-guide--matched' : ''
            }`}
            aria-hidden
          >
            <svg className="face-wellness__face-oval" viewBox="0 0 200 230" preserveAspectRatio="xMidYMid meet">
              <ellipse
                cx="100"
                cy="115"
                rx="78"
                ry="92"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeDasharray="6 7"
                strokeLinecap="round"
              />
            </svg>
          </div>
        ) : null}

        {phase === 'scanning' ? (
          <div
            className={`face-wellness__pose${poseFlash ? ' face-wellness__pose--flash' : ''}${
              poseMatched ? ' face-wellness__pose--matched' : ''
            }`}
            role="status"
            aria-live="polite"
          >
            <div
              className={`face-wellness__pose-arrow face-wellness__pose-arrow--${poseStep}`}
              key={poseStep}
            >
              <PoseArrow dir={poseStep} />
            </div>
            <p className="face-wellness__pose-text">
              {poseFlash
                ? t('faceWellness.pose.done')
                : t(`faceWellness.pose.${poseStep}`)}
            </p>
            {!poseFlash && poseMatched ? (
              <p className="face-wellness__pose-hold">{t('faceWellness.pose.hold')}</p>
            ) : null}
            {!poseFlash && poseStep !== 'front' ? (
              <div className="face-wellness__pose-bar" aria-hidden>
                <span style={{ width: `${poseHoldPct}%` }} />
              </div>
            ) : null}
          </div>
        ) : null}

        <header className="face-wellness__top">
          <LOGOSVG className="face-wellness__logo" aria-label="SoundPill" />
          <h1 className="face-wellness__title">{t('faceWellness.title')}</h1>
          <p className="face-wellness__lead">{t('faceWellness.subtitle')}</p>
        </header>

        {phase === 'error' && errorMsg ? (
          <p className="face-wellness__error" role="alert">
            {errorMsg}
          </p>
        ) : null}

        {showAnalyzing ? (
          <div className="face-wellness__analyzing" role="status" aria-live="polite">
            <div className="face-wellness__spinner" aria-hidden />
            <p className="face-wellness__analyzing-text">
              {phase === 'preparing' ? t('faceWellness.preparing') : t('faceWellness.analyzing')}
            </p>
            <p className="face-wellness__analyzing-sub">{t('faceWellness.analyzingHint')}</p>
          </div>
        ) : null}

        {phase === 'done' && result ? (
          <div className="face-wellness__results" aria-live="polite">
            <div className="face-wellness__glass">
              <article className="face-wellness__card">
                <p className="face-wellness__card-label">{t('faceWellness.ageLabel')}</p>
                <p className="face-wellness__card-value">
                  {result.age != null ? t('faceWellness.ageValue', { age: result.age }) : '—'}
                </p>
                <p className="face-wellness__card-sub">
                  {genderText ?? t('faceWellness.faceNotDetected')}
                  {emotionText ? ` · ${emotionText}` : ''}
                </p>
              </article>
              <article className="face-wellness__card">
                <p className="face-wellness__card-label">{t('faceWellness.heartLabel')}</p>
                <p className="face-wellness__card-value">
                  {result.heartRateBpm != null ? result.heartRateBpm : '—'}
                  {result.heartRateBpm != null ? <span> bpm</span> : null}
                </p>
                {result.heartRateBpm == null ? (
                  <p className="face-wellness__card-sub">{t('faceWellness.signalWeak')}</p>
                ) : null}
              </article>
              <article className="face-wellness__card face-wellness__card--accent">
                <p className="face-wellness__card-label">{t('faceWellness.stressLabel')}</p>
                <p className="face-wellness__card-value">{result.stressScore}</p>
                <p className="face-wellness__card-sub">
                  {t(`faceWellness.stress.${result.stressLevel}`)}
                </p>
              </article>
              <p className="face-wellness__disclaimer">{t('faceWellness.disclaimer')}</p>
              <button
                type="button"
                className="face-wellness__retry"
                onClick={() => void startScan()}
              >
                {t('faceWellness.retry')}
              </button>
            </div>
          </div>
        ) : null}

        <div className="face-wellness__shutter-wrap">
          {phase === 'scanning' ? (
            <p className="face-wellness__countdown">
              {t('faceWellness.secondsLeft', { count: remainSec })}
            </p>
          ) : null}
          <button
            type="button"
            className="face-wellness__shutter"
            onClick={handleShutter}
            disabled={phase === 'preparing' || phase === 'analyzing'}
            aria-label={
              phase === 'scanning'
                ? t('faceWellness.cancel')
                : phase === 'done'
                  ? t('faceWellness.retry')
                  : t('faceWellness.start')
            }
          >
            <svg
              className="face-wellness__ring"
              width={RING_SIZE}
              height={RING_SIZE}
              viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
              aria-hidden
            >
              <circle
                className="face-wellness__ring-track"
                cx={RING_SIZE / 2}
                cy={RING_SIZE / 2}
                r={RING_R}
                fill="none"
                strokeWidth={RING_STROKE}
              />
              <circle
                ref={ringProgressRef}
                className="face-wellness__ring-progress"
                cx={RING_SIZE / 2}
                cy={RING_SIZE / 2}
                r={RING_R}
                fill="none"
                stroke="#ffffff"
                strokeWidth={RING_STROKE}
                strokeDasharray={`${RING_C} ${RING_C}`}
                strokeDashoffset={RING_C}
                strokeLinecap="round"
                transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
                style={{
                  strokeDashoffset:
                    phase === 'scanning' || phase === 'analyzing'
                      ? RING_C * (1 - scanProgress / 100)
                      : RING_C,
                }}
              />
            </svg>
            <span
              className={`face-wellness__shutter-dot${
                phase === 'scanning' ? ' face-wellness__shutter-dot--active' : ''
              }`}
            />
          </button>
        </div>
      </div>
    </div>
  );
}
